import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Percent, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, 
  Plus, Settings, ChevronRight, IndianRupee, User, Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "₹0";
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

const ROLES = [
  { value: "sales_head", label: "Sales Head" },
  { value: "admin", label: "Admin" },
];

const DiscountApprovalsPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("requests");
  
  // Requests state
  const [requests, setRequests] = useState([]);
  const [pendingForMe, setPendingForMe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Configs state
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  
  // Config edit dialog
  const [configOpen, setConfigOpen] = useState(false);
  const [configForm, setConfigForm] = useState({
    project_id: "",
    threshold_percentage: 5,
    approval_chain: [{ step_order: 1, role: "sales_head", user_id: null }],
    is_active: true
  });
  
  // Review dialog
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewReq, setReviewReq] = useState(null);
  const [reviewAction, setReviewAction] = useState("");
  const [reviewComments, setReviewComments] = useState("");
  
  // Request detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReq, setDetailReq] = useState(null);

  useEffect(() => {
    if (tab === "requests") {
      loadRequests();
      loadPendingForMe();
    } else if (tab === "config") {
      loadConfigs();
      loadProjects();
    }
  }, [tab]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== "all" ? { status: filterStatus } : {};
      const res = await axios.get(`${API}/discount-approval/requests`, { params });
      setRequests(res.data);
    } catch (e) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  const loadPendingForMe = async () => {
    try {
      const res = await axios.get(`${API}/discount-approval/requests/pending`);
      setPendingForMe(res.data);
    } catch (e) {
      console.error("Failed to load pending for me");
    }
  };

  useEffect(() => {
    if (tab === "requests") loadRequests();
  }, [filterStatus]);

  const loadConfigs = async () => {
    setConfigsLoading(true);
    try {
      const res = await axios.get(`${API}/discount-approval/config`);
      setConfigs(res.data);
    } catch (e) {
      toast.error("Failed to load configs");
    } finally {
      setConfigsLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) {
      console.error("Failed to load projects");
    }
  };

  const handleReview = async () => {
    if (!reviewAction) {
      toast.error("Please select an action");
      return;
    }
    try {
      await axios.post(`${API}/discount-approval/requests/${reviewReq.request_id}/action`, {
        action: reviewAction,
        comments: reviewComments
      });
      toast.success(`Request ${reviewAction === "approve" ? "approved" : "rejected"}`);
      setReviewOpen(false);
      loadRequests();
      loadPendingForMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process");
    }
  };

  const saveConfig = async () => {
    if (!configForm.project_id) {
      toast.error("Please select a project");
      return;
    }
    try {
      await axios.post(`${API}/discount-approval/config`, configForm);
      toast.success("Configuration saved");
      setConfigOpen(false);
      loadConfigs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const editConfig = (config) => {
    setConfigForm({
      project_id: config.project_id,
      threshold_percentage: config.threshold_percentage || 5,
      approval_chain: config.approval_chain || [{ step_order: 1, role: "sales_head", user_id: null }],
      is_active: config.is_active !== false
    });
    setConfigOpen(true);
  };

  const addChainStep = () => {
    const lastStep = configForm.approval_chain.length;
    setConfigForm({
      ...configForm,
      approval_chain: [...configForm.approval_chain, { step_order: lastStep + 1, role: "admin", user_id: null }]
    });
  };

  const removeChainStep = (idx) => {
    const updated = configForm.approval_chain.filter((_, i) => i !== idx);
    // Re-number steps
    updated.forEach((s, i) => s.step_order = i + 1);
    setConfigForm({ ...configForm, approval_chain: updated });
  };

  const updateChainStep = (idx, field, value) => {
    const updated = [...configForm.approval_chain];
    updated[idx][field] = value;
    setConfigForm({ ...configForm, approval_chain: updated });
  };

  const canManageConfig = user?.role === "admin" || user?.role === "sales_head";
  const canReview = user?.role === "admin" || user?.role === "sales_head";

  // Filter requests by search term
  const filteredRequests = requests.filter(r => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (r.lead_name || "").toLowerCase().includes(s)
      || (r.lead_phone || "").toLowerCase().includes(s)
      || (r.lead_email || "").toLowerCase().includes(s)
      || (r.lead_id || "").toLowerCase().includes(s)
      || (r.project_name || "").toLowerCase().includes(s)
      || (r.requested_by_name || "").toLowerCase().includes(s);
  });

  const requestStats = {
    pending: requests.filter(r => r.status === "pending").length,
    approved: requests.filter(r => r.status === "approved").length,
    rejected: requests.filter(r => r.status === "rejected").length,
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="discount-approvals-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Discount Approvals</h1>
            <p className="text-sm text-slate-500 mt-1">Manage discount approval requests and configure thresholds</p>
          </div>
        </div>

        {/* Pending for me alert */}
        {pendingForMe.length > 0 && canReview && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-amber-800">{pendingForMe.length} requests awaiting your approval</p>
                <p className="text-xs text-amber-600">Click on a request to review and take action</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus("pending")}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-lg font-bold text-amber-600">{requestStats.pending}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus("approved")}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Approved</p>
                <p className="text-lg font-bold text-emerald-600">{requestStats.approved}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus("rejected")}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Rejected</p>
                <p className="text-lg font-bold text-red-600">{requestStats.rejected}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 h-10">
            <TabsTrigger value="requests" data-testid="requests-tab">Approval Requests</TabsTrigger>
            <TabsTrigger value="config" data-testid="config-tab">
              <Settings className="w-4 h-4 mr-1.5" />Configuration
            </TabsTrigger>
          </TabsList>

          {/* ===== REQUESTS ===== */}
          <TabsContent value="requests" className="space-y-4 mt-4">
            <div className="flex gap-2 items-center flex-wrap">
              <Input 
                placeholder="Search by name, phone, email, lead ID..." 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)} 
                className="w-[280px]"
                data-testid="discount-search"
              />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => { loadRequests(); loadPendingForMe(); }} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center text-slate-400">
                  <Percent className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>{searchTerm ? "No requests match your search" : "No discount approval requests"}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredRequests.map(r => {
                  const isPendingForMe = pendingForMe.some(p => p.request_id === r.request_id);
                  return (
                    <Card 
                      key={r.request_id} 
                      className={`hover:shadow-md transition-shadow cursor-pointer ${isPendingForMe ? "ring-2 ring-amber-300 bg-amber-50/30" : ""}`}
                      onClick={() => { setDetailReq(r); setDetailOpen(true); }}
                      data-testid={`request-card-${r.request_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-slate-900">{r.lead_name || "Lead"}</span>
                              <Badge className={`text-xs ${STATUS_STYLES[r.status]}`}>{r.status}</Badge>
                              <Badge variant="outline" className="text-xs text-red-600 border-red-200">
                                -{r.discount_percentage}%
                              </Badge>
                              {isPendingForMe && (
                                <Badge className="text-xs bg-amber-500 text-white">Action Required</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> {r.project_name || "N/A"}
                              </span>
                              <span>Original: {formatINR(r.original_price)}</span>
                              <span className="font-medium text-slate-700">
                                After Discount: {formatINR(r.discounted_price)}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" /> {r.requested_by_name}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            {r.status === "pending" && canReview && (
                              <Button 
                                size="sm" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setReviewReq(r); 
                                  setReviewAction(""); 
                                  setReviewComments(""); 
                                  setReviewOpen(true); 
                                }}
                                data-testid={`review-btn-${r.request_id}`}
                              >
                                Review
                              </Button>
                            )}
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ===== CONFIGURATION ===== */}
          <TabsContent value="config" className="space-y-4 mt-4">
            {canManageConfig && (
              <div className="flex justify-end">
                <Button 
                  size="sm" 
                  onClick={() => { 
                    setConfigForm({
                      project_id: "",
                      threshold_percentage: 5,
                      approval_chain: [{ step_order: 1, role: "sales_head", user_id: null }],
                      is_active: true
                    }); 
                    setConfigOpen(true); 
                  }}
                  data-testid="add-config-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Configuration
                </Button>
              </div>
            )}

            {configsLoading ? (
              <div className="text-center py-12 text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              </div>
            ) : configs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center text-slate-400">
                  <Settings className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No approval configurations yet</p>
                  <p className="text-xs mt-1">Configure discount thresholds per project</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {configs.map(c => (
                  <Card key={c.config_id} className="hover:shadow-md transition-shadow" data-testid={`config-card-${c.config_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{c.project_name || c.project_id}</span>
                            <Badge className={c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                              {c.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            <span>Threshold: <span className="font-medium text-slate-700">{c.threshold_percentage}%</span></span>
                            <span>Approval Chain: <span className="font-medium text-slate-700">{c.approval_chain?.length || 0} step(s)</span></span>
                          </div>
                          {c.approval_chain && c.approval_chain.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {c.approval_chain.map((step, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {step.step_order}. {step.role}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {canManageConfig && (
                          <Button variant="outline" size="sm" onClick={() => editConfig(c)} data-testid={`edit-config-${c.config_id}`}>
                            Edit
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
          <DialogContent className="sm:max-w-md" data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle>Review Discount Request</DialogTitle>
            </DialogHeader>
            {reviewReq && (
              <div className="space-y-4 py-2">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Lead:</span> <span className="font-medium">{reviewReq.lead_name}</span></div>
                    <div><span className="text-slate-500">Project:</span> <span className="font-medium">{reviewReq.project_name || "N/A"}</span></div>
                    <div><span className="text-slate-500">Original:</span> <span className="font-medium">{formatINR(reviewReq.original_price)}</span></div>
                    <div><span className="text-slate-500">Requested:</span> <span className="font-medium text-red-600">{formatINR(reviewReq.discounted_price)}</span></div>
                    <div><span className="text-slate-500">Discount:</span> <span className="font-bold text-red-600">{reviewReq.discount_percentage}%</span></div>
                    <div><span className="text-slate-500">Requested By:</span> <span className="font-medium">{reviewReq.requested_by_name}</span></div>
                  </div>
                  {reviewReq.justification && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-slate-500">Justification:</p>
                      <p className="text-sm text-slate-700">{reviewReq.justification}</p>
                    </div>
                  )}
                  {reviewReq.freebies && reviewReq.freebies.length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-slate-500">Freebies:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {reviewReq.freebies.map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button 
                    className={`p-4 rounded-lg border text-center transition-all ${reviewAction === "approve" ? "ring-2 ring-emerald-400 bg-emerald-50" : "hover:bg-slate-50"}`}
                    onClick={() => setReviewAction("approve")}
                    data-testid="approve-action"
                  >
                    <CheckCircle className="w-6 h-6 mx-auto mb-1 text-emerald-600" />
                    <span className="text-sm font-medium">Approve</span>
                  </button>
                  <button 
                    className={`p-4 rounded-lg border text-center transition-all ${reviewAction === "reject" ? "ring-2 ring-red-400 bg-red-50" : "hover:bg-slate-50"}`}
                    onClick={() => setReviewAction("reject")}
                    data-testid="reject-action"
                  >
                    <XCircle className="w-6 h-6 mx-auto mb-1 text-red-600" />
                    <span className="text-sm font-medium">Reject</span>
                  </button>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Comments</label>
                  <Textarea 
                    value={reviewComments} 
                    onChange={(e) => setReviewComments(e.target.value)} 
                    rows={2} 
                    placeholder={reviewAction === "reject" ? "Reason for rejection..." : "Optional comments..."}
                    data-testid="review-comments"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleReview} 
                disabled={!reviewAction}
                className={reviewAction === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : reviewAction === "reject" ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="submit-review-btn"
              >
                {reviewAction === "approve" ? "Approve" : reviewAction === "reject" ? "Reject" : "Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
            <DialogHeader>
              <DialogTitle>Discount Request Details</DialogTitle>
            </DialogHeader>
            {detailReq && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Lead:</span> <span className="font-semibold">{detailReq.lead_name}</span></div>
                  <div><span className="text-slate-500">Status:</span> <Badge className={`text-xs ${STATUS_STYLES[detailReq.status]}`}>{detailReq.status}</Badge></div>
                  <div><span className="text-slate-500">Project:</span> <span className="font-medium">{detailReq.project_name || "N/A"}</span></div>
                  <div><span className="text-slate-500">Requested By:</span> <span className="font-medium">{detailReq.requested_by_name}</span></div>
                  <div><span className="text-slate-500">Original Price:</span> <span className="font-medium">{formatINR(detailReq.original_price)}</span></div>
                  <div><span className="text-slate-500">Discounted Price:</span> <span className="font-medium text-red-600">{formatINR(detailReq.discounted_price)}</span></div>
                  <div><span className="text-slate-500">Discount Amount:</span> <span className="font-medium">{formatINR(detailReq.discount_amount)}</span></div>
                  <div><span className="text-slate-500">Discount %:</span> <span className="font-bold text-red-600">{detailReq.discount_percentage}%</span></div>
                </div>

                {detailReq.justification && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Justification</p>
                    <p className="text-sm text-slate-700">{detailReq.justification}</p>
                  </div>
                )}

                {detailReq.freebies && detailReq.freebies.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Freebies Included</p>
                    <div className="flex flex-wrap gap-1">
                      {detailReq.freebies.map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}
                    </div>
                  </div>
                )}

                {/* Approval Chain Status */}
                {detailReq.approval_chain && detailReq.approval_chain.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Approval Chain</p>
                    <div className="space-y-2">
                      {detailReq.approval_chain.map((step, i) => {
                        const approval = detailReq.approvals?.find(a => a.step === step.step_order);
                        return (
                          <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${approval ? (approval.status === "approve" ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200") : detailReq.current_step === step.step_order ? "bg-amber-50 border-amber-200" : "bg-slate-50"}`}>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: approval ? (approval.status === "approve" ? "#d1fae5" : "#fee2e2") : "#f1f5f9" }}>
                              {step.step_order}
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium capitalize">{step.role}</span>
                              {approval && (
                                <p className="text-xs text-slate-500">
                                  {approval.status === "approve" ? "Approved" : "Rejected"} by {approval.approved_by_name}
                                </p>
                              )}
                            </div>
                            {approval ? (
                              approval.status === "approve" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />
                            ) : detailReq.current_step === step.step_order ? (
                              <Clock className="w-4 h-4 text-amber-600" />
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Final decision */}
                {detailReq.status !== "pending" && detailReq.final_decision_by_name && (
                  <div className={`rounded-lg p-3 ${detailReq.status === "approved" ? "bg-emerald-50" : "bg-red-50"}`}>
                    <p className="text-xs text-slate-500 mb-1">Final Decision</p>
                    <p className="text-sm font-medium">
                      {detailReq.status === "approved" ? "Approved" : "Rejected"} by {detailReq.final_decision_by_name}
                    </p>
                    {detailReq.rejection_reason && (
                      <p className="text-sm text-slate-600 mt-1">{detailReq.rejection_reason}</p>
                    )}
                  </div>
                )}

                {/* Action button for pending */}
                {detailReq.status === "pending" && canReview && pendingForMe.some(p => p.request_id === detailReq.request_id) && (
                  <Button 
                    className="w-full" 
                    onClick={() => { 
                      setDetailOpen(false); 
                      setReviewReq(detailReq); 
                      setReviewAction(""); 
                      setReviewComments(""); 
                      setReviewOpen(true); 
                    }}
                  >
                    Review This Request
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Config Dialog */}
        <Dialog open={configOpen} onOpenChange={setConfigOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="config-dialog">
            <DialogHeader>
              <DialogTitle>Discount Approval Configuration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Project *</label>
                <Select value={configForm.project_id} onValueChange={(v) => setConfigForm({ ...configForm, project_id: v })}>
                  <SelectTrigger data-testid="config-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Discount Threshold (%)</label>
                <p className="text-xs text-slate-500 mb-2">Discounts above this percentage require approval</p>
                <Input 
                  type="number" 
                  value={configForm.threshold_percentage} 
                  onChange={(e) => setConfigForm({ ...configForm, threshold_percentage: parseFloat(e.target.value) || 0 })}
                  min={0}
                  max={100}
                  step={0.5}
                  data-testid="config-threshold"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Approval Chain</label>
                  <Button variant="outline" size="sm" onClick={addChainStep} data-testid="add-chain-step">
                    <Plus className="w-3 h-3 mr-1" /> Add Step
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mb-2">Define who must approve in sequence</p>
                <div className="space-y-2">
                  {configForm.approval_chain.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">
                        {step.step_order}
                      </div>
                      <Select value={step.role} onValueChange={(v) => updateChainStep(idx, "role", v)}>
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {configForm.approval_chain.length > 1 && (
                        <Button variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => removeChainStep(idx)}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="config-active" 
                  checked={configForm.is_active}
                  onChange={(e) => setConfigForm({ ...configForm, is_active: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <label htmlFor="config-active" className="text-sm">Active (enforce approval workflow)</label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancel</Button>
              <Button onClick={saveConfig} data-testid="save-config-btn">Save Configuration</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DiscountApprovalsPage;
